import { decodeJwt } from "jose";
import React from "react";

import { fromUnixTime, isPast } from "date-fns";
import * as Cookies from "js-cookie";
import * as analytics from "@/services/analytics";
import { useLocation } from "react-router";
import { isNotLoginRequired } from "@/helpers/auth";
import * as Sentry from "@sentry/react";
import { useToggle } from "react-use";
import { SessionTimeoutWarningModal } from "@/components/modals/session-timeout-warning-modal";

interface Props {
  children: React.ReactNode;
}

const VERYFI_TOKEN = "__VERYFI_TOKEN___".toLowerCase() !== "false";

const tokenCookieName = "pf-access-token";
const SESSION_CHECK_INTERVAL = 3000;
const SESSION_EXPIRATION_THRESHOLD = 60 * 2;

export const AuthProvider: React.FC<Props> = ({ children }) => {
  const { pathname } = useLocation();
  const [isFirstLoading, setIsFirstLoading] = React.useState(true);
  const [showSessionTimeoutModal, setShowSessionTimeoutModal] = useToggle(false);

  const checkSessionStatus = React.useCallback(async () => {
    if (!VERYFI_TOKEN) {
      return;
    }
    try {
      const cookie = Cookies.get(tokenCookieName);
      if (isNotLoginRequired(pathname)) {
        setIsFirstLoading(false);

        return;
      }

      if (!cookie) {
        const state = btoa(pathname);
        const nextUrl = `/login?state=${state}`;
        document.location.href = `/pf-auth/logout?next_url=${encodeURIComponent(nextUrl)}`;
      } else {
        const jwtDecoded = decodeJwt(cookie);
        if (jwtDecoded.exp && isPast(fromUnixTime(jwtDecoded.exp))) {
          analytics.sessionTimeout();
          document.location.href = "/pf-auth/logout?next_url=/session-expired";
        } else if (jwtDecoded.exp && !showSessionTimeoutModal && isAlmostExpired(fromUnixTime(jwtDecoded.exp))) {
          setShowSessionTimeoutModal(true);
        } else {
          setIsFirstLoading(false);
        }
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  }, [pathname, setShowSessionTimeoutModal, showSessionTimeoutModal]);

  React.useEffect(() => {
    // check is user authenticated every 30 seconds
    const interval = setInterval(() => {
      void checkSessionStatus();
    }, SESSION_CHECK_INTERVAL);
    void checkSessionStatus();

    return () => clearInterval(interval);
  }, [checkSessionStatus]);

  if (isFirstLoading) {
    return null;
  }

  return (
    <>
      {children}
      {showSessionTimeoutModal && <SessionTimeoutWarningModal onRefresh={() => setShowSessionTimeoutModal()} />}
    </>
  );
};

const isAlmostExpired = (expirationDate: Date) => {
  const now = new Date();
  const diff = expirationDate.getTime() - now.getTime();
  return diff < SESSION_EXPIRATION_THRESHOLD * 1000;
};
